import { useError, useGetAiDiscoverabilityReports, useGetAssessmentTemplates, useDiscoverSubjects, useLoader, } from "@piiqtechnologies/ui-lib";
import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useAuthContext } from "./AuthContext";
import { buildCommunityTemplateRows } from "@utils/communityTemplateRows";
const AiDiscoverabilityContext = createContext(undefined);
export const AiDiscoverabilityProvider = ({ children }) => {
    const { setError } = useError();
    const { setLoader } = useLoader();
    const { user } = useAuthContext();
    const [fetchReports, reportsResponse, loadingReports, errorLoadingReports] = useGetAiDiscoverabilityReports();
    const [fetchTemplates, templatesResponse, loadingTemplates, errorLoadingTemplates] = useGetAssessmentTemplates();
    const [fetchSubjects, subjectsResponse, loadingSubjects, errorLoadingSubjects] = useDiscoverSubjects();
    const fetchAll = useCallback(() => {
        if (user) {
            fetchReports();
            fetchTemplates({ limit: 3000 });
            fetchSubjects({ limit: 3000 });
        }
    }, [user, fetchReports, fetchTemplates, fetchSubjects]);
    useEffect(() => {
        fetchAll();
    }, [fetchAll]);
    const isLoading = loadingReports || loadingTemplates || loadingSubjects;
    const error = errorLoadingReports || errorLoadingTemplates || errorLoadingSubjects;
    useEffect(() => {
        setLoader(isLoading, "loadingAiDiscoverability");
        return () => setLoader(false, "loadingAiDiscoverability");
    }, [isLoading]);
    useEffect(() => {
        if (error) {
            setError(error === null || error === void 0 ? void 0 : error.message);
        }
    }, [error]);
    const communities = useMemo(() => {
        var _a, _b;
        const templates = templatesResponse !== null && templatesResponse !== void 0 ? templatesResponse : [];
        const subjects = (_a = subjectsResponse === null || subjectsResponse === void 0 ? void 0 : subjectsResponse.subjects) !== null && _a !== void 0 ? _a : [];
        const reports = reportsResponse !== null && reportsResponse !== void 0 ? reportsResponse : [];
        if (!templates.length)
            return [];
        const subjectMap = new Map(subjects.map((s) => [s._id, s]));
        const reportMap = new Map(reports.map((r) => [r.overview.subjectId, r]));
        const grouped = new Map();
        for (const template of templates) {
            const existing = (_b = grouped.get(template.subjectId)) !== null && _b !== void 0 ? _b : [];
            existing.push(template);
            grouped.set(template.subjectId, existing);
        }
        return Array.from(grouped.entries()).map(([subjectId, subjectTemplates]) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            const subject = subjectMap.get(subjectId);
            const reportData = reportMap.get(subjectId);
            const subjectName = (_c = (_a = subject === null || subject === void 0 ? void 0 : subject.name) !== null && _a !== void 0 ? _a : (_b = reportData === null || reportData === void 0 ? void 0 : reportData.overview) === null || _b === void 0 ? void 0 : _b.subjectName) !== null && _c !== void 0 ? _c : "Unknown Community";
            return {
                subjectId,
                subjectName,
                subjectAddress: (_f = (_d = subject === null || subject === void 0 ? void 0 : subject.address) !== null && _d !== void 0 ? _d : (_e = reportData === null || reportData === void 0 ? void 0 : reportData.overview) === null || _e === void 0 ? void 0 : _e.subjectAddress) !== null && _f !== void 0 ? _f : "",
                autoReportEnabled: (_g = subject === null || subject === void 0 ? void 0 : subject.autoReportEnabled) !== null && _g !== void 0 ? _g : false,
                templates: buildCommunityTemplateRows({
                    templates: subjectTemplates,
                    reports: reportData === null || reportData === void 0 ? void 0 : reportData.reports,
                    subjectId,
                    subjectName,
                    fallbackLatestReportDate: (_j = (_h = reportData === null || reportData === void 0 ? void 0 : reportData.overview) === null || _h === void 0 ? void 0 : _h.createdOn) !== null && _j !== void 0 ? _j : null,
                }),
            };
        });
    }, [templatesResponse, subjectsResponse, reportsResponse]);
    const activeTemplatesCount = useMemo(() => {
        return (templatesResponse !== null && templatesResponse !== void 0 ? templatesResponse : []).filter((t) => t.isActive).length;
    }, [templatesResponse]);
    const totalTemplatesCount = useMemo(() => {
        return (templatesResponse !== null && templatesResponse !== void 0 ? templatesResponse : []).length;
    }, [templatesResponse]);
    const runningAssessmentIds = useMemo(() => {
        var _a;
        const ids = [];
        for (const community of communities) {
            for (const template of community.templates) {
                if (template.status === "running") {
                    const id = (_a = (templatesResponse !== null && templatesResponse !== void 0 ? templatesResponse : []).find((t) => t._id === template.templateId)) === null || _a === void 0 ? void 0 : _a.lastAssessmentId;
                    if (id)
                        ids.push(id);
                }
            }
        }
        return ids;
    }, [communities, templatesResponse]);
    const value = useMemo(() => ({
        reportsData: reportsResponse || [],
        communities,
        isLoading,
        error,
        refetchReports: fetchReports,
        refetchAll: fetchAll,
        activeTemplatesCount,
        totalTemplatesCount,
        runningAssessmentIds,
    }), [
        reportsResponse,
        communities,
        isLoading,
        error,
        fetchReports,
        fetchAll,
        activeTemplatesCount,
        totalTemplatesCount,
        runningAssessmentIds,
    ]);
    return React.createElement(AiDiscoverabilityContext.Provider, { value: value }, children);
};
export const useAiDiscoverabilityContext = () => {
    const context = useContext(AiDiscoverabilityContext);
    if (context === undefined) {
        throw new Error("useAiDiscoverabilityContext must be used within an AiDiscoverabilityProvider");
    }
    return context;
};
